import { useMemo, useState } from "react"

import { CheckIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import {
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/pricing-errors"
import { SelectField } from "~/components/ui-components/v2"

import {
  btnPrimary,
  btnSecondary,
  monthlyUsd,
  Pill,
  sectionLabelClass,
  type Tone,
} from "./financing-chrome"
import type {
  CatalogLoanFinancingView,
  FinancingKindBundle,
  LoanProductAvailability,
  LoanProductOption,
  LoanProductQuoteView,
  LoanProductView,
} from "./financing-kind-bundle"
import { OutOfDateNotice } from "../../ProposalPresentation/pricing/pricing-out-of-date"

export const NOT_CALCULATED_LABEL = `Not yet calculated`
export const PAYMENT_UNAVAILABLE_LABEL = `Payment unavailable`
export const PAYMENT_OUT_OF_DATE_LABEL = `Out of date`

const AVAILABILITY_LABEL: Record<LoanProductAvailability, string> = {
  eligible: `Eligible`,
  inactive: `No longer offered`,
  missing_payment_factor: `Cannot calculate a payment`,
  not_active_in_state: `Not offered in this state`,
  outside_amount_limits: `Price outside limits`,
  wrong_finance_partner: `Different Finance Partner`,
}

const usd = new Intl.NumberFormat(`en-US`, {
  style: `currency`,
  currency: `USD`,
  maximumFractionDigits: 0,
})

const formatUsd = (value: number | null): string =>
  value == null ? `—` : usd.format(value)

const termsSummary = (product: LoanProductView): string =>
  `${product.termYears}-year` +
  (product.apr == null ? `` : ` · ${product.apr.toFixed(2)}% APR`) +
  (product.dealerFee == null
    ? ``
    : ` · ${product.dealerFee.toFixed(1)}% dealer fee`)

const optionLabel = (product: LoanProductView, showTerms: boolean): string =>
  showTerms ? `${product.name} · ${termsSummary(product)}` : product.name

/**
 * The payment cell for a product. A Quote only speaks for the product it was
 * priced with, so anything else reads as not yet calculated. Payments are
 * shown exactly as the server returned them; nothing is estimated here.
 */
const paymentCell = (
  quote: LoanProductQuoteView | null,
  productId: string,
): React.ReactNode => {
  if (quote == null || quote.loanProduct?.id !== productId) {
    return <span className="text-bp-text-dim">{NOT_CALCULATED_LABEL}</span>
  }
  if (quote.estimatedMonthlyPayment == null) {
    return <span className="text-bp-danger">{PAYMENT_UNAVAILABLE_LABEL}</span>
  }

  return (
    <span className="inline-flex items-center gap-2">
      {quote.status === `stale` ? (
        <OutOfDateNotice label={PAYMENT_OUT_OF_DATE_LABEL} />
      ) : null}
      <span>{`${monthlyUsd.format(quote.estimatedMonthlyPayment)}/mo`}</span>
    </span>
  )
}

const LoanProductDetails = ({
  product,
  quote,
  saved,
}: {
  product: LoanProductView
  quote: LoanProductQuoteView | null
  saved: boolean
}): JSX.Element => {
  // Snapshot Term/APR only when a payment is retained (stale/current). An
  // unavailable Quote has no payment to keep honest, so show live catalog terms.
  const pricedTerms =
    quote?.loanProduct?.id === product.id && quote.status !== `unavailable`
      ? quote.loanProduct
      : product
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: `Term`,
      value: `${pricedTerms.termYears} years`,
    },
    {
      label: `APR`,
      value: pricedTerms.apr == null ? `—` : `${pricedTerms.apr.toFixed(2)}%`,
    },
    {
      label: `Dealer fee`,
      value:
        product.dealerFee == null ? `—` : `${product.dealerFee.toFixed(1)}%`,
    },
    { label: `Minimum loan`, value: formatUsd(product.minimumLoanAmount) },
    { label: `Maximum loan`, value: formatUsd(product.maximumLoanAmount) },
    {
      label: `Estimated monthly payment`,
      value: paymentCell(quote, product.id),
    },
  ]

  return (
    <div className="rounded-lg border border-bp-border bg-bp-elevated">
      <div className="flex items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
        <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
          {product.name}
        </span>
        {saved ? (
          <Pill tone="muted">Saved</Pill>
        ) : (
          <Pill tone="warn">Unsaved</Pill>
        )}
      </div>
      <table className="w-full text-[length:var(--step--2)]">
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.label}
              className={clsx(index > 0 && `border-t border-bp-border/60`)}
            >
              <th
                scope="row"
                className="px-4 py-2.5 text-left font-medium text-bp-text-muted"
              >
                {row.label}
              </th>
              <td className="px-4 py-2.5 text-right text-bp-text tabular-nums">
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const OptionLabel = ({
  product,
  showTerms,
}: {
  product: LoanProductOption
  showTerms: boolean
}): JSX.Element => {
  const label = optionLabel(product, showTerms)
  if (product.availability === `eligible`) {
    return <>{label}</>
  }
  return (
    <>
      {label}
      <span className="block text-[length:var(--step--2)] normal-case">
        {product.unavailableReason ?? AVAILABILITY_LABEL[product.availability]}
      </span>
    </>
  )
}

/**
 * Loan Products drawn from the synced catalog — Credit Human today. The
 * seller picks one and the kind's save records it; Battery's Save & Calculate
 * Payment also prices it through PowurOS.
 */
export const CatalogLoanProductPicker = <F extends CatalogLoanFinancingView>({
  bundle,
  proposalId,
  financing,
  onClose,
}: {
  bundle: Pick<FinancingKindBundle<F>, `loanProduct`>
  proposalId: string
  financing: F | null
  /** The drawer's Close; a host with its own way on (the Summary) omits it. */
  onClose?: () => void
}): JSX.Element => {
  const { options: optionsOf, showOptionTerms, copy } = bundle.loanProduct
  const save = bundle.loanProduct.useSave()
  // Draft lives only here; closing the drawer discards it. The server read is
  // the fallback until the seller picks something.
  const [draftId, setDraftId] = useState<string | null>(null)
  const [saved, setSaved] = useState<`complete` | `without_payment` | null>(null)

  const quote = financing?.quote ?? null
  const selected = financing?.selectedLoanProduct ?? null
  const options = useMemo(
    () => (financing ? optionsOf(financing) : []),
    [financing, optionsOf],
  )
  const retained =
    selected != null &&
    (selected.availability !== `eligible` ||
      financing?.selectionStatus === `selected_product_unavailable`)
  const retainedTone: Tone =
    selected?.availability === `outside_amount_limits` ? `warn` : `danger`
  const retainedAvailabilityLabel =
    financing?.selectionStatus === `selected_product_unavailable` &&
    selected?.availability === `eligible`
      ? `Unavailable`
      : selected
        ? AVAILABILITY_LABEL[selected.availability]
        : null
  // Also the id the save submits: recalculating an unchanged
  // selection is a legitimate retry, but a retained unavailable product is not
  // something to price again.
  const effectiveId =
    draftId ?? (selected != null && !retained ? selected.id : null)
  const current = options.find((product) => product.id === effectiveId) ?? null
  const dirty = draftId != null && draftId !== selected?.id
  const canEdit = financing?.actions.selectLoanProduct.allowed ?? false
  const saving = save.isLoading
  const showPicker =
    financing != null &&
    financing.selectionStatus !== `finance_partner_required` &&
    financing.selectionStatus !== `no_loan_product_required`

  const choose = (product: LoanProductOption): void => {
    if (!canEdit || saving) {
      return
    }
    save.reset()
    setSaved(null)
    setDraftId(product.id)
  }

  const submit = (): void => {
    if (!canEdit || saving || effectiveId == null) {
      return
    }
    save.mutate(
      { proposalId, body: { loanProductId: effectiveId } },
      {
        onSuccess: (financing) => {
          setDraftId(null)
          // The endpoint succeeds with an unavailable Poisoned Quote too, so
          // the confirmation must reflect what came back, not the 200.
          setSaved(
            copy.savedWithoutPaymentLabel != null &&
              financing.quote?.estimatedMonthlyPayment == null
              ? `without_payment`
              : `complete`,
          )
        },
      },
    )
  }

  return (
    <>
      {showPicker ? (
        <div className="flex flex-col gap-3">
          <h3 className={sectionLabelClass}>Loan Product</h3>

          {retained && selected ? (
            <div
              className={clsx(
                `flex items-center justify-between gap-3 rounded-lg border px-3.5 py-3`,
                retainedTone === `warn`
                  ? `border-bp-gold/40 bg-bp-gold/5`
                  : `border-bp-danger/40 bg-bp-danger/5`,
              )}
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[length:var(--step--1)] font-semibold text-bp-text">
                  {selected.name}
                </span>
                <span className="text-[length:var(--step--2)] text-bp-text-muted">
                  {termsSummary(selected)}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <Pill tone="muted">Saved</Pill>
                <Pill tone={retainedTone}>{retainedAvailabilityLabel}</Pill>
              </span>
            </div>
          ) : null}

          <SelectField
            label="Loan Product"
            labelSrOnly
            portal={false}
            disabled={!canEdit || saving}
            options={options}
            value={current}
            onChange={choose}
            getKey={(product) => product.id}
            getLabel={(product) => optionLabel(product, showOptionTerms)}
            renderOption={(product) => (
              <OptionLabel product={product} showTerms={showOptionTerms} />
            )}
            isOptionDisabled={(product) => product.availability !== `eligible`}
            placeholder={
              !options.some((product) => product.availability === `eligible`)
                ? `No eligible Loan Products`
                : `Select a Loan Product`
            }
          />

          {!canEdit && financing.actions.selectLoanProduct.reason ? (
            <p className="text-[length:var(--step--2)] text-bp-text-muted">
              {financing.actions.selectLoanProduct.reason}
            </p>
          ) : null}

          {current ? (
            <LoanProductDetails
              product={current}
              quote={dirty ? null : quote}
              saved={!dirty && selected?.id === current.id}
            />
          ) : null}

          {!dirty &&
          quote?.status === `unavailable` &&
          // Only speak for the product this Quote was priced with; another
          // product's Poisoned Quote says nothing about this selection.
          quote.loanProduct?.id === effectiveId ? (
            <p
              role="alert"
              className="rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2 text-[length:var(--step--2)] text-bp-danger"
            >
              {copy.paymentUnavailablePrompt}
              {/* Only promise the retry to someone who can reach it: a
                  read-only actor has no button, and a retained product is
                  not something to price again. */}
              {canEdit && effectiveId != null && copy.paymentRetryPrompt != null
                ? ` ${copy.paymentRetryPrompt}`
                : ``}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-auto flex shrink-0 flex-col gap-3 border-t border-bp-border pt-5">
        {save.isError ? (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2"
          >
            <span className="text-[length:var(--step--2)] text-bp-danger">
              {isPricingSupersededError(save.error)
                ? PRICING_SUPERSEDED_MESSAGE
                : copy.saveErrorPrompt}
            </span>
            <button
              type="button"
              onClick={submit}
              className="shrink-0 rounded-md border border-bp-danger/50 px-3 py-1 text-[length:var(--step--2)] font-medium text-bp-text"
            >
              Retry
            </button>
          </div>
        ) : null}
        {saved ? (
          <p
            role="status"
            className={clsx(
              `flex items-center gap-1.5 text-[length:var(--step--2)] font-medium`,
              saved === `complete` ? `text-bp-success` : `text-bp-text-muted`,
            )}
          >
            <CheckIcon className="size-4 shrink-0" aria-hidden="true" />
            {saved === `without_payment`
              ? copy.savedWithoutPaymentLabel
              : copy.savedLabel}
          </p>
        ) : null}
        <div
          className={clsx(
            `flex items-center gap-4`,
            onClose ? `justify-between` : `justify-end`,
          )}
        >
          {onClose ? (
            <button type="button" onClick={onClose} className={btnSecondary}>
              Close
            </button>
          ) : null}
          {showPicker && canEdit ? (
            <button
              type="button"
              disabled={effectiveId == null || saving}
              onClick={submit}
              className={btnPrimary}
            >
              {saving ? copy.savingLabel : copy.saveLabel}
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}
